import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { clsx } from "clsx";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

type Tipo = "exito" | "error" | "info";
type Toast = { id: number; tipo: Tipo; texto: string };
type Api = { exito: (t: string) => void; error: (t: string) => void; info: (t: string) => void };

const Ctx = createContext<Api | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [lista, setLista] = useState<Toast[]>([]);
  const n = useRef(0);
  const push = useCallback((tipo: Tipo, texto: string) => {
    const id = ++n.current;
    setLista((l) => [...l.slice(-3), { id, tipo, texto }]);
    window.setTimeout(() => setLista((l) => l.filter((t) => t.id !== id)), tipo === "error" ? 7000 : 3500);
  }, []);
  const api = useMemo<Api>(() => ({
    exito: (t) => push("exito", t),
    error: (t) => push("error", t),
    info: (t) => push("info", t),
  }), [push]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4 md:bottom-6 md:items-end md:pr-6" aria-live="polite">
        {lista.map((t) => (
          <div key={t.id} className={clsx(
            "pointer-events-auto flex max-w-md items-start gap-2 rounded-md px-4 py-3 text-sm shadow-lg",
            t.tipo === "exito" && "bg-emerald-600 text-white",
            t.tipo === "error" && "bg-red-600 text-white",
            t.tipo === "info" && "bg-sidebar text-white",
          )} role={t.tipo === "error" ? "alert" : "status"}>
            {t.tipo === "exito" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : t.tipo === "error" ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> : <Info className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{t.texto}</span>
            <button onClick={() => setLista((l) => l.filter((x) => x.id !== t.id))} aria-label="Cerrar aviso" className="ml-1 opacity-80 hover:opacity-100"><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): Api {
  const api = useContext(Ctx);
  if (!api) throw new Error("useToast fuera de ToastProvider");
  return api;
}
